/**
 * The signup address field: Google Places autocomplete plus a working
 * "use my current location" button.
 *
 * It replaces a plain text box whose location link only ever stored
 * coordinates — the label stayed empty, so "Location captured" sat directly
 * above "Address is required".
 *
 * The web equivalent of the app's `AddressPicker`, minus the map. Both end at
 * the same pair: a formatted address string and the coordinates behind it.
 *
 * Progressive enhancement is deliberate. Places needs the Maps JS SDK, but
 * the location button uses the REST geocoder, and plain typing needs neither.
 * If the script fails to load — wrong key, offline, Places not enabled on the
 * project — the field degrades to a text input rather than dead-ending
 * signup on an optional convenience.
 */

import { Suspense, lazy, memo, useCallback, useEffect, useRef, useState } from "react";
import { FastField, getIn } from "formik";
import { Input } from "antd";
import { useLoadScript } from "@react-google-maps/api";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";

import { GOOGLE_MAP_KEY } from "../../../../utils/configs";
import {
  getCurrentPosition,
  locationErrorMessage,
  reverseGeocode,
} from "../../geocoding";

/**
 * Module scope, and identical to the options `HexMap` loads with. The Maps
 * loader is a singleton: a second call with different options throws
 * "Loader must not be called again with different options", and a fresh array
 * literal per render makes the loader warn on every one.
 */
const PLACES_LIBRARIES = ["places"];

const LocationDialog = lazy(() =>
  import("../../../../components/Location/LocationDialog")
);

const PinIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z"
    />
  </svg>
);

const MapIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinejoin="round"
      d="M9 4 3.5 6v14L9 18l6 2 5.5-2V4L15 6 9 4Zm0 0v14m6-12v14"
    />
  </svg>
);

/**
 * Owns the Places hooks, so they sit outside the `FastField` render prop
 * where hooks are not allowed.
 */
const AddressBox = ({
  id,
  value,
  coords,
  invalid,
  placeholder,
  mapPicker,
  onType,
  onResolve,
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAP_KEY,
    libraries: PLACES_LIBRARIES,
  });

  const {
    init,
    ready,
    suggestions: { data },
    setValue: setQuery,
    clearSuggestions,
  } = usePlacesAutocomplete({ initOnMount: false, debounce: 300 });

  const [open, setOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [pickingOnMap, setPickingOnMap] = useState(false);

  const boxRef = useRef(null);

  // `initOnMount: false` — calling the hook before the SDK is on the page
  // throws, and the script is still loading on first render.
  useEffect(() => {
    if (isLoaded) init();
  }, [isLoaded, init]);

  // A click anywhere else dismisses the list.
  useEffect(() => {
    if (!open) return undefined;

    const onDocumentClick = (event) => {
      if (!boxRef.current?.contains(event.target)) setOpen(false);
    };

    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [open]);

  const handleType = (event) => {
    const next = event.target.value;

    // Free text invalidates whatever coordinates were captured: they would
    // otherwise still point at the previous address.
    onType(next);
    setLocationError("");

    if (ready) {
      setQuery(next);
      setOpen(true);
    }
  };

  const handleSelect = async (description) => {
    setQuery(description, false);
    clearSuggestions();
    setOpen(false);

    try {
      const results = await getGeocode({ address: description });
      const { lat, lng } = await getLatLng(results[0]);

      onResolve(description, { latitude: lat, longitude: lng });
    } catch {
      // The label is what the user chose and is worth keeping even when the
      // follow-up geocode fails; only the coordinates are lost.
      onResolve(description, null);
    }
  };

  const useMyLocation = useCallback(async () => {
    setLocating(true);
    setLocationError("");

    try {
      const { latitude, longitude } = await getCurrentPosition();
      const address = await reverseGeocode(latitude, longitude);

      if (ready) setQuery(address, false);
      clearSuggestions();
      setOpen(false);

      onResolve(address, { latitude, longitude });
    } catch (caught) {
      setLocationError(locationErrorMessage(caught));
    } finally {
      setLocating(false);
    }
  }, [clearSuggestions, onResolve, ready, setQuery]);

  const handleMapSave = (picked) => {
    setPickingOnMap(false);
    if (!picked) return;

    const label = picked.address1 || value;

    if (ready) setQuery(label, false);
    clearSuggestions();
    setOpen(false);
    setLocationError("");

    onResolve(label, { latitude: picked.latitude, longitude: picked.longitude });
  };

  const showList = open && data.length > 0;

  return (
    <div className="auth-address" ref={boxRef}>
      <Input
        id={id}
        size="large"
        value={value}
        onChange={handleType}
        onFocus={() => data.length && setOpen(true)}
        onKeyDown={(event) => event.key === "Escape" && setOpen(false)}
        placeholder={placeholder}
        autoComplete="off"
        status={invalid ? "error" : undefined}
      />

      {showList && (
        <ul className="auth-suggestions" role="listbox">
          {data.map(({ place_id: placeId, description }) => (
            <li key={placeId}>
              <button
                type="button"
                role="option"
                aria-selected="false"
                className="auth-suggestion"
                // `onMouseDown`: a plain click would blur the input and the
                // outside-click handler would close the list before it fired.
                onMouseDown={(event) => {
                  event.preventDefault();
                  handleSelect(description);
                }}
              >
                {description}
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="auth-meta auth-meta--left auth-address-actions">
        <button
          type="button"
          className="auth-linkish auth-locate"
          onClick={useMyLocation}
          disabled={locating}
        >
          <PinIcon />
          {locating ? "Finding your location…" : "Use my current location"}
        </button>

        {mapPicker && (
          <button
            type="button"
            className="auth-linkish auth-locate"
            onClick={() => setPickingOnMap(true)}
          >
            <MapIcon />
            Choose on map
          </button>
        )}
      </p>

      {pickingOnMap && (
        <Suspense fallback={null}>
          <LocationDialog
            open
            value={coords || {}}
            addressText={coords ? value : ""}
            onClose={() => setPickingOnMap(false)}
            onSave={handleMapSave}
          />
        </Suspense>
      )}

      {locationError && (
        <span className="auth-field-error" role="alert">
          {locationError}
        </span>
      )}

      {loadError && !locationError && (
        <span className="auth-hint">
          Address suggestions are unavailable. You can still type your address.
        </span>
      )}
    </div>
  );
};

/**
 * Writes two values: the label the user sees, and the coordinates behind it.
 * They move together on purpose — the old screen could hold one without the
 * other.
 */
const AddressField = memo(function AddressField({
  name,
  coordsName,
  label,
  placeholder,
  mapPicker = false,
}) {
  const id = `auth-${name}`;

  return (
    <FastField name={name}>
      {({ field, form, meta }) => {
        const error = meta.touched && meta.error ? meta.error : "";

        return (
          <div className={`auth-field${error ? " auth-field--invalid" : ""}`}>
            <label className="auth-label" htmlFor={id}>
              {label}
            </label>

            <AddressBox
              id={id}
              value={field.value}
              coords={getIn(form.values, coordsName)}
              mapPicker={mapPicker}
              invalid={Boolean(error)}
              placeholder={placeholder}
              /**
               * Ordering is load-bearing. Only the LAST call validates, and
               * Formik validates against the value that call carries — so the
               * address has to go last. With it first, locating filled the
               * field and still left "Address is required" showing, because
               * the run that produced that error had not seen the new text.
               *
               * `coords` is not in the schema, so its writes never validate.
               */
              onType={(text) => {
                form.setFieldValue(coordsName, null, false);
                form.setFieldValue(name, text, true);
              }}
              onResolve={(text, coords) => {
                form.setFieldValue(coordsName, coords, false);
                form.setFieldTouched(name, true, false);
                form.setFieldValue(name, text, true);
              }}
            />

            {error && (
              <span className="auth-field-error" role="alert">
                {error}
              </span>
            )}
          </div>
        );
      }}
    </FastField>
  );
});

export default AddressField;
